import tenant from '../../assets/img/icons/common/tenant.png';
import location from '../../assets/img/icons/common/location.png';
import subOrg from '../../assets/img/icons/common/subOrg.png';
import { StructuralAccountType } from './StructuralAccountType';
import { SquareCheckbox } from "../../components/Inputs/SquareCheckbox/SquareCheckbox";
import { RoundedButton, ButtonColor, ButtonCorner } from "../../components/Buttons/RoundedButton";
import DeleteButton from '../../components/Buttons/DeleteButton';
import { LocationModalContext } from '../../utilities/Contexts/LocationModalContext'; 
import { useContext, useState } from 'react'; 
import { locationModalTypes, treeTypes } from '../../constants/treeState';
import Button from '../../components/UIButton/UIButton';
import { AccountIdType } from '../../constants/AccountIdType';

const TreeNode = ({ node, handleCheckboxClick, onAddOrgClick, onEditOrgClick, onEditTenantClick, showActionButtons = true, treeSize, selectAll, deselectAll }) => {
    const {
        toggleLocationModal,
        setSelectedAccountId,
        setLocationModalType,
        setModalTreeCheckedItem,
        locationModalType }
        = useContext(LocationModalContext);

    const context = node.context;
    const isChecked = context.viewStateFlags.checkedItems;
    let isDisabled = context.viewStateFlags.disabledItems;

    //mini-tree is showing and the modal type is addLocation, then disable the checkbox
    if (treeSize === treeTypes.Mini && locationModalType === locationModalTypes.Add) {
        isDisabled = true;
    }

    const checkBoxClassName = treeSize === treeTypes.Mini ? "mini-tree-checkbox" : "";

    // Determine the icon based on accountType
    const structuralAccountTypeId = node.item.data.structuralAccountTypeId;
    const isGhostTenant = structuralAccountTypeId === StructuralAccountType.Tenant && node.item.accountId === AccountIdType.GhostAccountId;

    const getNodeTitle = () => {

        if (!node) return "";

        // keep normal behavior
        if (node.item?.accountId !== AccountIdType.GhostAccountId) {
            return node.title;
        }

        // special title for the ghost/root node
        return node?.item?.data?.name;
    };
    let icon;
    let altText;
    
    switch (structuralAccountTypeId) {
        case StructuralAccountType.Tenant:
            icon = tenant;
            altText = 'tenant';
            break;
        case StructuralAccountType.Suborg:
            icon = subOrg;
            altText = 'subOrg';
            break;
        case StructuralAccountType.Location:
            icon = location;
            altText = 'location';
            break;
        default:
            icon = '❓';
    }
    

    const handleCheck = (event) =>
    {
        setModalTreeCheckedItem([node.item.accountId]);
        handleCheckboxClick(node);
    }
    const addOrEditLocationBtnClickHandler = (action) => {
        setSelectedAccountId(node.item.accountId);
        setLocationModalType(action);
        if (action === locationModalTypes.Add) {
            setModalTreeCheckedItem([node.item.accountId]);
        } else {
            setModalTreeCheckedItem([node.item.data.parentId.toString()]);
        }
        toggleLocationModal();
    }
    
    return (
        <div className="d-flex align-items-center w-100">
            {/* LEFT cluster: checkbox + icon + title + Select/Deselect All */}
            <div className="d-flex align-items-center gap-3 flex-shrink-0">
                {structuralAccountTypeId !== StructuralAccountType.Tenant && (
                    <SquareCheckbox
                        value={isChecked}
                        valueChangeHandler={handleCheck}
                        disabled={isDisabled}
                        checkboxClassName={checkBoxClassName}
                    />
                )}

                {treeSize === treeTypes.Mini &&
                    (structuralAccountTypeId === StructuralAccountType.Tenant && !isGhostTenant) && (
                        <SquareCheckbox
                            value={isChecked}
                            valueChangeHandler={handleCheck}
                            disabled={isDisabled}
                            checkboxClassName={checkBoxClassName}
                        />
                    )}

                <div className="text-center">
                    <img src={icon} alt={altText} className="w-75 h-75 img-fluid" />
                </div>

                {treeSize === treeTypes.FullSize &&
                    structuralAccountTypeId === StructuralAccountType.Tenant && (
                        <span className="badge background-color-teal px-3 text-white">
                            HQ
                        </span>
                    )}

                {getNodeTitle()}

                {treeSize === treeTypes.FullSize &&
                    structuralAccountTypeId === StructuralAccountType.Tenant && (
                        <div className="d-flex align-items-center gap-2 ml-3">
                            <Button as="div" variant="primary" size="small" onClick={selectAll}>
                                Select All
                            </Button>
                            <Button
                                as="div"
                                variant="primary"
                                size="small"
                                onClick={deselectAll}
                            >
                                Deselect All
                            </Button>
                        </div>
                    )}
            </div>

            {/* RIGHT cluster: action buttons, pushed to the far right */}
            {showActionButtons && (
                <div
                    className="tree-btn-container d-flex align-items-center gap-3 ml-auto"
                    style={{ gap: "15px" }}
                >
                    {structuralAccountTypeId === StructuralAccountType.Tenant && !isGhostTenant && (
                        <Button
                            as="div"
                            id="EditHQBtn"
                            variant="primary"
                            size="small"
                            onClick={() => {
                                onEditTenantClick(node);
                            }}
                        >
                            Edit HQ Info
                        </Button>
                    )}

                    {structuralAccountTypeId === StructuralAccountType.Suborg && (
                        <Button
                            as="div"
                            id="EditOrgBtn"
                            variant="primary"
                            size="small"
                            onClick={() =>
                                onEditOrgClick(
                                    node.item.data.accountId,
                                    node.item.data.parentId,
                                    node.item.data.name
                                )
                            }
                        >
                            Edit
                        </Button>
                    )}

                    {structuralAccountTypeId === StructuralAccountType.Location && (
                        <Button
                            as="div"
                            id="EditLocBtn"
                            variant="primary"
                            size="small"
                            onClick={() => {
                                addOrEditLocationBtnClickHandler(locationModalTypes.Edit);
                            }}
                        >
                            Edit Location
                        </Button>
                    )}

                    {((structuralAccountTypeId === StructuralAccountType.Tenant && !isGhostTenant) ||
                        structuralAccountTypeId === StructuralAccountType.Suborg) && (
                            <>
                                <Button
                                    as="div"
                                    id="AddLocBtn"
                                    variant="primary"
                                    size="small"
                                    onClick={() => {
                                        addOrEditLocationBtnClickHandler(locationModalTypes.Add);
                                    }}
                                >
                                    + Add Location
                                </Button>

                                <Button
                                    as="div"
                                    id="AddOrgBtn"
                                    variant="primary"
                                    size="small"
                                    onClick={() => onAddOrgClick(node.item.data.accountId)}
                                >
                                    + Add Organization
                                </Button>
                            </>
                        )}
                </div>
            )}
        </div>
    );

};

export default TreeNode;
